import "./i18n";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import { fetchTenProducts, fetchTop8Products } from "./redux/slices/productsSlice";
import { fetchUserCart, loadGuestCartFromStorage, selectIsAuthenticated } from "./redux/slices/cartSlice";
import { initGA, trackPageview } from "./analytics";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

import Addresses from "./components/Profile/Addresses/Addresses";
import CitySelectModal from "./components/CitySelectModal/CitySelectModal";
import Favorites from "./components/Profile/Favorites/Favorites";
import Footer from "./components/Footer/Footer";
import { Loader } from "./components/Loader/Loader";
// import { MetaPixel } from "./utils/MetaPixel";
import MyOrders from "./components/Profile/MyOrders/MyOrders";
import Nav from "./components/Header/Nav/Nav";
import PasswordManager from "./components/Profile/PasswordManager/PasswordManager";
import PaymentMethod from "./components/Profile/PaymentMethod/PaymentMethod";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import ScrollToTopWindow from "./components/ScrollToTop/ScrollToTopWindow";
// import { SnapPixel } from "./utils/SnapPixel";
// import { TiktokPixel } from "./utils/TiktokPixel";
import { Toaster } from 'react-hot-toast';
import UpdateProfile from "./components/Profile/GeneralInformation/UpdateProfile";
import { fetchBranches } from "./redux/slices/branchesSlice";
import { fetchCatalogs } from "./redux/slices/catalogsSlice";
import { fetchCategories } from "./redux/slices/categoriesSlice";
import { fetchFavorites } from "./redux/slices/favoritesSlice";
import { fetchNewProducts } from "./redux/slices/productNewsSlice";
import { setCity } from "./redux/slices/citySelectionSlice";
import { useTranslation } from "react-i18next";

// import GoogleAdsPixel from "./utils/GoogleAdsPixel";




















const Home = lazy(() => import("./pages/Home"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Products = lazy(() => import("./pages/Products"));
const Product = lazy(() => import("./pages/Product"));
const Profile = lazy(() => import("./pages/Profile"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Cart = lazy(() => import("./pages/Cart"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Catalog = lazy(() => import("./pages/Catalog"));
const TopSelling = lazy(() => import("./pages/TopSelling"));
const Category = lazy(() => import("./pages/Category"));
const FreshPicks = lazy(() => import("./pages/FreshPicks"));
const OrderCompleted = lazy(() => import("./pages/OrderCompleted"));
const TrackOrder = lazy(() => import("./components/TrackOrder/TrackOrder"));
const Contact = lazy(() => import("./pages/Contact"));
const Faq = lazy(() => import("./pages/Faq"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const TrackOrderById = lazy(() => import("./pages/TrackOrderById"));
const OrderFailure = lazy(() => import("./pages/OrderFailure"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));


function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [showCityModal, setShowCityModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location]);

  const { i18n } = useTranslation();

  useEffect(() => {
    const preload = async () => {
      dispatch(fetchCategories());
      dispatch(fetchCatalogs());
      dispatch(fetchTenProducts());
      dispatch(fetchTop8Products());
      dispatch(fetchNewProducts({ sortId: "recent-to-old", page: 1 }));
      dispatch(fetchBranches());
    };

    preload();
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchFavorites());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    const savedCity = localStorage.getItem("selectedCity");
    if (!savedCity) {
      setShowCityModal(true);
    } else {
      dispatch(setCity(JSON.parse(savedCity)));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserCart());
    } else {
      dispatch(loadGuestCartFromStorage());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <div dir={i18n.language === "en" ? "ltr" : "rtl"}
      className={`flex flex-col w-full items-center justify-center 
    overflow-x-clip text-blue-2 ${i18n.language === "ar"
          ? "font-tajawal" : "font-goli"}`}>
      {/* <MetaPixel />
      <TiktokPixel />
      <SnapPixel /> */}
      {/* <GoogleAdsPixel /> */}
      <Nav />
      <ScrollToTopWindow />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="general-info" />} />
            <Route path="general-info" element={<UpdateProfile />} />
            <Route path="my-orders" element={<MyOrders />} />
            <Route path="payment-method" element={<PaymentMethod />} />
            <Route path="addresses" element={<Addresses />} />
            <Route path="password-manager" element={<PasswordManager />} />
            <Route path="favorites" element={<Favorites />} />
          </Route>
          <Route path="/categories/:categoryId" element={<Category />} />
          <Route path="/products" element={<Products />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/catalogs/:catalogId" element={<Catalog />} />
          <Route path="/products/:productId" element={<Product />} />
          <Route path="/:categoryId/:productId" element={<Product />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/fresh-picks" element={<FreshPicks />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/track-order" element={
            <ProtectedRoute>
              <TrackOrderById />
            </ProtectedRoute>
          } />
          <Route path="/faqs" element={<Faq />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/top-selling-products" element={<TopSelling />} />
          <Route path="/order/success/:orderId" element={
            <ProtectedRoute>
              <OrderCompleted />
            </ProtectedRoute>
          } />
          <Route path="/order/failure/:orderId" element={
            <ProtectedRoute>
              <OrderFailure />
            </ProtectedRoute>
          } />
          <Route path="/order/order-details/:orderId" element={
            <ProtectedRoute>
              <OrderDetails />
            </ProtectedRoute>
          } />
          <Route path="/track-order/:orderId" element={
            <ProtectedRoute>
              <TrackOrder />
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster position="top-center" reverseOrder={false} />
      <Footer />
      {
        showCityModal && (
          <CitySelectModal onSelect={() => {
            setShowCityModal(false);
          }} />
        )
      }
      {/* ValueLeadMe tracking pixel */}
      <iframe src="https://track.valueleadme.com/pixel?adid=66d9416934685b2a97342780" scrolling="no" frameBorder="0" width="1" height="1" style={{ display: 'none' }} title="ValueLeadMe Pixel" />
      {/* Trackier tracking pixel */}
      <iframe src="https://theballyhoo.gotrackier.com/pixel?adid=6874bcc9f324c43a092d0232" scrolling="no" frameBorder="0" width="1" height="1" style={{ display: 'none' }} title="Trackier Pixel" />
    </div>
  );
}

export default App;
